import React, { useEffect, useMemo, useState } from 'react';
import { experiments as expApi } from '../api/client';
import ReportViewer from '../workspace/ui/ReportViewer';
import {
  buildPublicReportAppUrl,
  buildPublicReportOgUrl,
  buildPublicReportPath,
  buildPublicReportStructuredData,
  buildPublicReportUrl,
  getReportAuthorLabel,
  getReportDescription,
  slugifyReportTitle,
} from '../utils/publicReports';

const C = {
  void: '#020508',
  deep: '#060b14',
  surface: '#0a1120',
  raised: '#0d1626',
  b0: '#0f1a2e',
  b1: '#1e293b',
  b2: '#334155',
  t1: '#e2e8f0',
  t2: '#b6c3d8',
  t3: '#8396b3',
  t4: '#627795',
  cyan: '#22d3ee',
  violet: '#a78bfa',
  green: '#4ade80',
  gold: '#fbbf24',
  red: '#f87171',
  mono: "'JetBrains Mono',monospace",
  display: "'Syne',sans-serif",
};

function extractReportRoute(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (parts[0] !== 'reports' || !parts[1]) return null;
  return {
    id: parts[1],
    slug: parts[2] || '',
  };
}

function setMetaTag(attribute, key, content) {
  if (typeof document === 'undefined') return;
  let node = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attribute, key);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content);
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function compactNumber(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '0';
  if (numeric >= 1000000) return `${(numeric / 1000000).toFixed(numeric >= 10000000 ? 0 : 1)}M`;
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(numeric >= 10000 ? 0 : 1)}K`;
  return `${numeric}`;
}

function colorFromText(text) {
  const { _livePalette } = require('../context/paletteSync');
  const palette = _livePalette.slice(0, 4);
  const source = String(text || 'jorvex');
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) & 0xffff;
  }
  return palette[Math.abs(hash) % palette.length];
}

function normalizeSuggestion(raw) {
  const summary = raw?.feed_summary || {};
  const dataset = summary.dataset || raw?.snapshot?.dataset || {};
  const social = raw?.social || {};
  return {
    id: raw?.id || '',
    title: raw?.title || 'Untitled analysis',
    type: raw?.type || 'auto_eda',
    description: getReportDescription(raw),
    datasetName: dataset.name || '',
    task: dataset.task || '',
    rows: Number(dataset.rows || 0),
    cols: Number(dataset.cols || 0),
    createdAt: raw?.created_at || raw?.updated_at || '',
    href: buildPublicReportAppUrl(raw?.id, raw?.slug || raw?.title || ''),
    views: Number(raw?.view_count || 0),
    upvotes: Number(social.upvotes || 0),
    comments: Number(social.comments_count || 0),
    qualityScore: typeof summary.quality_score === 'number' ? summary.quality_score : null,
    reportReady: !!summary.report_ready,
  };
}

function recommendationReason(item, currentReport, index) {
  const currentDataset = currentReport?.feed_summary?.dataset || currentReport?.snapshot?.dataset || {};
  if (item.type === currentReport?.type && item.task && item.task === currentDataset.task) {
    return 'Same analysis lane and task signal';
  }
  if (item.type === currentReport?.type) {
    return 'Same analysis engine, different angle';
  }
  if (item.datasetName && currentDataset.name && item.datasetName === currentDataset.name) {
    return 'Same dataset, alternate take';
  }
  if (item.reportReady && (item.qualityScore || 0) >= 80) {
    return 'High-quality public report';
  }
  return index === 0 ? 'Trending next read' : 'Recommended from the public feed';
}

function LoadingExperience() {
  return (
    <div style={{
      minHeight: '72vh',
      display: 'grid',
      placeItems: 'center',
      border: `1px solid ${C.b1}`,
      borderRadius: 22,
      background: `linear-gradient(180deg, ${C.surface}, rgba(6,11,20,0.98))`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(circle at 18% 18%, ${C.cyan}14, transparent 34%), radial-gradient(circle at 84% 12%, ${C.violet}12, transparent 28%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ position: 'relative', textAlign: 'center', padding: '40px 24px', maxWidth: 520 }}>
        <div className="jorvex-public-spinner-shell">
          <div className="jorvex-public-spinner-ring ring-a" />
          <div className="jorvex-public-spinner-ring ring-b" />
          <div className="jorvex-public-spinner-core" />
        </div>
        {/* <div style={{ fontFamily: C.mono, fontSize: 10, color: C.cyan, letterSpacing: '.2em', margin: '18px 0 10px' }}>
          LOADING PUBLIC REPORT
        </div> */}
        <div style={{ fontFamily: C.display, fontWeight: 900, fontSize: 'clamp(28px, 5vw, 48px)', lineHeight: 1.02, marginBottom: 10 }}>
           Loading...
        </div>
       
      </div>
    </div>
  );
}

function SuggestionCard({ item, reason, featured = false }) {
  const accent = colorFromText(item.type || item.title);
  return (
    <a
      href={item.href}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: featured ? 18 : 14,
        border: `1px solid ${featured ? `${accent}55` : C.b1}`,
        background: featured
          ? `linear-gradient(180deg, ${accent}18, rgba(10,17,32,0.98))`
          : `linear-gradient(180deg, rgba(13,22,38,0.96), rgba(6,11,20,0.96))`,
        padding: featured ? '16px 16px 14px' : '14px 14px 12px',
        boxShadow: featured ? `0 18px 40px ${accent}18` : '0 12px 28px rgba(0,0,0,.22)',
        transition: 'transform .18s ease, border-color .18s ease, box-shadow .18s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: C.mono, fontSize: 8, color: accent, letterSpacing: '.16em', marginBottom: 6 }}>
            {featured ? 'EDITOR PICK' : 'RECOMMENDED'}
          </div>
          <div style={{ fontFamily: C.display, fontWeight: 800, fontSize: featured ? 20 : 16, color: C.t1, lineHeight: 1.08 }}>
            {item.title}
          </div>
        </div>
        {item.qualityScore != null && (
          <div style={{
            minWidth: 46,
            padding: '6px 7px',
            borderRadius: 999,
            border: `1px solid ${accent}44`,
            background: `${accent}14`,
            textAlign: 'center',
          }}>
            <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.14em' }}>Q</div>
            <div style={{ fontFamily: C.display, fontSize: 16, fontWeight: 900, color: accent }}>{item.qualityScore}</div>
          </div>
        )}
      </div>

      <div style={{ fontFamily: C.mono, fontSize: 8, color: C.t4, letterSpacing: '.12em', marginBottom: 8 }}>
        {reason.toUpperCase()}
      </div>
      <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.75, marginBottom: 12 }}>
        {item.description}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ padding: '4px 7px', borderRadius: 999, border: `1px solid ${C.b1}`, background: C.deep, color: C.t3, fontFamily: C.mono, fontSize: 8 }}>
          {String(item.type || '').replace(/_/g, ' ').toUpperCase()}
        </span>
        {item.datasetName ? (
          <span style={{ padding: '4px 7px', borderRadius: 999, border: `1px solid ${C.b1}`, background: C.deep, color: C.t3, fontFamily: C.mono, fontSize: 8 }}>
            {item.datasetName}
          </span>
        ) : null}
        {item.task ? (
          <span style={{ padding: '4px 7px', borderRadius: 999, border: `1px solid ${accent}44`, background: `${accent}14`, color: accent, fontFamily: C.mono, fontSize: 8 }}>
            {String(item.task).toUpperCase()}
          </span>
        ) : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
        <div style={{ padding: '8px 9px', borderRadius: 10, background: C.deep, border: `1px solid ${C.b0}` }}>
          <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 3 }}>VIEWS</div>
          <div style={{ fontFamily: C.display, fontSize: 16, fontWeight: 900, color: C.t1 }}>{compactNumber(item.views)}</div>
        </div>
        <div style={{ padding: '8px 9px', borderRadius: 10, background: C.deep, border: `1px solid ${C.b0}` }}>
          <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 3 }}>UPVOTES</div>
          <div style={{ fontFamily: C.display, fontSize: 16, fontWeight: 900, color: accent }}>{compactNumber(item.upvotes)}</div>
        </div>
        <div style={{ padding: '8px 9px', borderRadius: 10, background: C.deep, border: `1px solid ${C.b0}` }}>
          <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 3 }}>COMMENTS</div>
          <div style={{ fontFamily: C.display, fontSize: 16, fontWeight: 900, color: C.violet }}>{compactNumber(item.comments)}</div>
        </div>
      </div>

      <div style={{ marginTop: 11, fontFamily: C.mono, fontSize: 8, color: C.t4, letterSpacing: '.12em' }}>
        {item.createdAt ? `PUBLISHED ${relativeTime(item.createdAt).toUpperCase()}` : 'PUBLICLY AVAILABLE'}
      </div>
    </a>
  );
}

export default function PublicReportPage({ currentPath, onAuthOpen }) {
  const route = useMemo(() => extractReportRoute(currentPath), [currentPath]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copyState, setCopyState] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestionsError, setSuggestionsError] = useState('');

  useEffect(() => {
    if (!route?.id) {
      setError('Invalid report path');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    expApi.getOne(route.id)
      .then((data) => {
        if (cancelled) return;
        setReport(data?.experiment || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.data?.error || err?.message || 'Failed to load public report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [route?.id]);

  useEffect(() => {
    if (!route?.id) return undefined;

    let cancelled = false;
    setSuggestionsLoading(true);
    setSuggestionsError('');

    expApi.publicFeed({ limit: 18 })
      .then((data) => {
        if (cancelled) return;
        setSuggestions((data?.experiments || []).map(normalizeSuggestion));
      })
      .catch((err) => {
        if (cancelled) return;
        setSuggestionsError(err?.data?.error || err?.message || 'Could not load more analyses');
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [route?.id]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflowX = document.body.style.overflowX;
    const prevBodyOverflowY = document.body.style.overflowY;
    const prevBodyHeight = document.body.style.height;
    const prevBodyMinHeight = document.body.style.minHeight;
    const prevHtmlOverflowX = document.documentElement.style.overflowX;
    const prevHtmlOverflowY = document.documentElement.style.overflowY;
    const prevHtmlHeight = document.documentElement.style.height;
    const prevHtmlMinHeight = document.documentElement.style.minHeight;
    const rootNode = document.getElementById('root');
    const prevRootOverflow = rootNode?.style.overflow || '';
    const prevRootHeight = rootNode?.style.height || '';
    const prevRootMinHeight = rootNode?.style.minHeight || '';
    document.body.style.overflow = 'auto';
    document.body.style.overflowX = 'hidden';
    document.body.style.overflowY = 'auto';
    document.body.style.height = 'auto';
    document.body.style.minHeight = '100%';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.overflowY = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.minHeight = '100%';
    if (rootNode) {
      rootNode.style.overflow = 'visible';
      rootNode.style.height = 'auto';
      rootNode.style.minHeight = '100vh';
    }

    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.overflowX = prevBodyOverflowX;
      document.body.style.overflowY = prevBodyOverflowY;
      document.body.style.height = prevBodyHeight;
      document.body.style.minHeight = prevBodyMinHeight;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.overflowX = prevHtmlOverflowX;
      document.documentElement.style.overflowY = prevHtmlOverflowY;
      document.documentElement.style.height = prevHtmlHeight;
      document.documentElement.style.minHeight = prevHtmlMinHeight;
      if (rootNode) {
        rootNode.style.overflow = prevRootOverflow;
        rootNode.style.height = prevRootHeight;
        rootNode.style.minHeight = prevRootMinHeight;
      }
    };
  }, [currentPath, route?.id]);

  useEffect(() => {
    if (typeof document === 'undefined' || !report) return undefined;

    const slug = report.slug || slugifyReportTitle(report.title || route?.slug || 'report');
    const canonicalPath = buildPublicReportPath(report.id, slug);
    const canonicalUrl = buildPublicReportUrl(report.id, slug);
    const ogImageUrl = buildPublicReportOgUrl(report.id, slug);
    const description = getReportDescription(report);
    const title = `${report.title || 'Public Report'} | Jorvex`;

    document.title = title;

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    setMetaTag('name', 'description', description);
    setMetaTag('name', 'robots', 'index,follow');
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', 'article');
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', ogImageUrl);
    setMetaTag('property', 'og:image:alt', `${report.title || 'Jorvex report'} social preview`);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImageUrl);

    let structured = document.head.querySelector('script[data-jorvex-public-report="true"]');
    if (!structured) {
      structured = document.createElement('script');
      structured.type = 'application/ld+json';
      structured.setAttribute('data-jorvex-public-report', 'true');
      document.head.appendChild(structured);
    }
    structured.textContent = JSON.stringify(buildPublicReportStructuredData(report, canonicalUrl));

    if (typeof window !== 'undefined') {
      const query = new URLSearchParams(window.location.search);
      const enteredViaAppBridge = !!query.get('public_report');
      if (!enteredViaAppBridge && window.location.pathname.startsWith('/reports/') && window.location.pathname !== canonicalPath) {
        window.history.replaceState(window.history.state, '', canonicalPath);
      }
    }

    return () => {
      const active = document.head.querySelector('script[data-jorvex-public-report="true"]');
      if (active) active.remove();
    };
  }, [report, route?.slug]);

  const shareUrl = report ? buildPublicReportAppUrl(report.id, report.slug || report.title || '') : '';
  const interactiveUrl = report ? buildPublicReportAppUrl(report.id, report.slug || report.title || '') : '';
  const currentDataset = report?.feed_summary?.dataset || report?.snapshot?.dataset || {};
  const topMetrics = useMemo(() => {
    const summary = report?.feed_summary || {};
    const social = report?.social || {};
    return [
      { label: 'VIEWS', value: Number(report?.view_count || 0).toLocaleString(), color: C.cyan },
      { label: 'UPVOTES', value: Number(social.upvotes || 0).toLocaleString(), color: C.green },
      { label: 'COMMENTS', value: Number(social.comments_count || 0).toLocaleString(), color: C.violet },
      { label: 'TYPE', value: String(report?.type || 'analysis').replace(/_/g, ' ').toUpperCase(), color: C.gold },
      { label: 'REPORT', value: summary.report_ready ? 'READY' : 'PUBLISHED', color: C.t2 },
    ];
  }, [report]);

  const reportInsights = useMemo(() => {
    const summaryInsights = Array.isArray(report?.feed_summary?.insights) ? report.feed_summary.insights.filter(Boolean).slice(0, 4) : [];
    if (summaryInsights.length) return summaryInsights;
    const fallback = [];
    if (currentDataset?.name) fallback.push(`${currentDataset.name} is the active dataset in this published report.`);
    if (currentDataset?.task) fallback.push(`This report is aligned to a ${String(currentDataset.task).toLowerCase()} task context.`);
    if (report?.social?.comments_count) fallback.push(`Community discussion is already forming around this analysis.`);
    if (report?.feed_summary?.report_ready) fallback.push('The full rendered report is available below for long-form inspection.');
    return fallback.slice(0, 4);
  }, [currentDataset, report]);

  const railFacts = useMemo(() => ([
    { label: 'DATASET', value: currentDataset?.name || 'Published analysis' },
    { label: 'SHAPE', value: currentDataset?.rows ? `${Number(currentDataset.rows).toLocaleString()} x ${currentDataset?.cols || 0}` : 'Snapshot ready' },
    { label: 'TASK', value: currentDataset?.task ? String(currentDataset.task).toUpperCase() : 'EXPLORATORY' },
    { label: 'QUALITY', value: report?.feed_summary?.quality_score != null ? `${report.feed_summary.quality_score}/100` : 'LIVE' },
  ]), [currentDataset, report]);

  const recommendedReports = useMemo(() => {
    if (!report) return [];
    const currentDatasetName = String(currentDataset?.name || '').toLowerCase();
    return suggestions
      .filter((item) => item.id && item.id !== report.id)
      .sort((left, right) => {
        const leftSameType = left.type === report.type ? 1 : 0;
        const rightSameType = right.type === report.type ? 1 : 0;
        if (leftSameType !== rightSameType) return rightSameType - leftSameType;

        const leftSameDataset = currentDatasetName && String(left.datasetName || '').toLowerCase() === currentDatasetName ? 1 : 0;
        const rightSameDataset = currentDatasetName && String(right.datasetName || '').toLowerCase() === currentDatasetName ? 1 : 0;
        if (leftSameDataset !== rightSameDataset) return rightSameDataset - leftSameDataset;

        const leftScore = (left.reportReady ? 8 : 0) + left.upvotes * 2 + left.comments * 3 + left.views * 0.15 + (left.qualityScore || 0);
        const rightScore = (right.reportReady ? 8 : 0) + right.upvotes * 2 + right.comments * 3 + right.views * 0.15 + (right.qualityScore || 0);
        return rightScore - leftScore;
      })
      .slice(0, 5);
  }, [currentDataset, report, suggestions]);

  const handleCopy = async () => {
    if (!shareUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('COPIED');
      window.setTimeout(() => setCopyState(''), 1600);
    } catch (_) {
      setCopyState('FAILED');
      window.setTimeout(() => setCopyState(''), 1600);
    }
  };

  if (!route?.id) {
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, ${C.void}, ${C.deep})`, color: C.t1, overflowX: 'hidden' }}>
      <style>{`
        @keyframes jorvexPublicSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes jorvexPublicPulse {
          0%, 100% { transform: scale(.92); opacity: .45; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .jorvex-public-spinner-shell {
          position: relative;
          width: 118px;
          height: 118px;
          margin: 0 auto;
        }
        .jorvex-public-spinner-ring {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          border-style: solid;
          border-color: transparent;
        }
        .jorvex-public-spinner-ring.ring-a {
          border-width: 3px;
          border-top-color: ${C.cyan};
          border-right-color: rgba(34,211,238,.28);
          animation: jorvexPublicSpin 1.1s linear infinite;
        }
        .jorvex-public-spinner-ring.ring-b {
          inset: 17px;
          border-width: 2px;
          border-bottom-color: ${C.violet};
          border-left-color: rgba(167,139,250,.26);
          animation: jorvexPublicSpin 1.7s linear infinite reverse;
        }
        .jorvex-public-spinner-core {
          position: absolute;
          inset: 43px;
          border-radius: 999px;
          background: linear-gradient(135deg, ${C.cyan}, ${C.violet});
          box-shadow: 0 0 28px rgba(34,211,238,.42);
          animation: jorvexPublicPulse 1.4s ease-in-out infinite;
        }
        .jorvex-public-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(320px, 390px);
          gap: 18px;
          align-items: start;
        }
        .jorvex-public-rec-card:hover {
          transform: translateY(-3px);
          border-color: ${C.cyan};
          box-shadow: 0 22px 46px rgba(0,0,0,.34);
        }
        @media (max-width: 1080px) {
          .jorvex-public-layout {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      `}</style>

      <div style={{ maxWidth: 1360, margin: '0 auto', padding: '24px 18px 60px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <a href="/" style={{ color: C.t3, textDecoration: 'none', fontFamily: C.mono, fontSize: 9, letterSpacing: '.12em' }}>
            {'<- JORVEX'}
          </a>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCopy}
              style={{ padding: '9px 12px', borderRadius: 6, border: `1px solid ${C.b1}`, background: C.surface, color: copyState === 'COPIED' ? C.green : C.t2, cursor: 'pointer', fontFamily: C.mono, fontSize: 9, fontWeight: 800, letterSpacing: '.12em' }}
            >
              {copyState || 'COPY LIVE LINK'}
            </button>
            <button
              type="button"
              onClick={() => onAuthOpen?.('signup')}
              style={{ padding: '9px 12px', borderRadius: 6, border: `1px solid ${C.cyan}44`, background: `${C.cyan}12`, color: C.cyan, cursor: 'pointer', fontFamily: C.mono, fontSize: 9, fontWeight: 800, letterSpacing: '.12em' }}
            >
              BUILD IN JORVEX
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingExperience />
        ) : error ? (
          <div style={{ border: `1px solid ${C.red}44`, borderRadius: 12, background: `${C.red}10`, padding: '30px 24px' }}>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.red, letterSpacing: '.16em', marginBottom: 10 }}>REPORT UNAVAILABLE</div>
            <div style={{ fontFamily: C.display, fontWeight: 900, fontSize: 30, color: C.t1, marginBottom: 8 }}>This public report could not be loaded</div>
            <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.8, marginBottom: 14 }}>{error}</div>
            <a href="/" style={{ color: C.cyan, fontFamily: C.mono, fontSize: 9, letterSpacing: '.12em', textDecoration: 'none' }}>GO BACK HOME</a>
          </div>
        ) : report ? (
          <>
            

            <section style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: C.mono, fontSize: 9, color: C.cyan, letterSpacing: '.16em', marginBottom: 8 }}>PUBLIC JORVEX REPORT</div>
              <ReportViewer exp={report} pageMode />
            </section>

            <section style={{
              borderRadius: 22,
              border: `1px solid ${C.b1}`,
              background: `linear-gradient(180deg, rgba(10,17,32,0.98), rgba(6,11,20,0.98))`,
              overflow: 'hidden',
              boxShadow: '0 20px 50px rgba(0,0,0,.22)',
            }}>
              <div style={{ padding: '18px 18px 14px', borderBottom: `1px solid ${C.b1}`, background: `linear-gradient(135deg, ${C.cyan}16, transparent 60%)` }}>
                <div style={{ fontFamily: C.mono, fontSize: 9, color: C.cyan, letterSpacing: '.18em', marginBottom: 8 }}>
                  MORE ANALYSES TO EXPLORE
                </div>
                
              
              </div>

              <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {suggestionsLoading ? (
                  [0, 1, 2, 3].map((index) => (
                    <div key={index} style={{
                      height: index === 0 ? 220 : 190,
                      borderRadius: 16,
                      border: `1px solid ${C.b1}`,
                      background: `linear-gradient(90deg, ${C.raised}, ${C.deep}, ${C.raised})`,
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.6s linear infinite',
                    }} />
                  ))
                ) : suggestionsError ? (
                  <div style={{ padding: '12px 12px', borderRadius: 14, border: `1px solid ${C.red}44`, background: `${C.red}10`, color: C.red, fontFamily: C.mono, fontSize: 9 }}>
                    {suggestionsError}
                  </div>
                ) : recommendedReports.length > 0 ? (
                  recommendedReports.map((item, index) => (
                    <div key={item.id} className="jorvex-public-rec-card">
                      <SuggestionCard
                        item={item}
                        reason={recommendationReason(item, report, index)}
                        featured={index === 0}
                      />
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '14px 12px', borderRadius: 14, border: `1px solid ${C.b1}`, background: C.raised, color: C.t3, fontSize: 10, lineHeight: 1.7 }}>
                    More public analyses will appear here as soon as the feed returns related reports.
                  </div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
